import { getGiftBagConfig, GIFT_BAG_PROPERTIES } from './gift-bag-config.js';

class GiftBagManager {
  constructor() {
    this.config = getGiftBagConfig();
    this.isProcessing = false;
    this.init();
  }

  init() {
    if (!this.config.enabled || !this.config.productHandle) {
      console.log('[GiftBagManager DEBUG] Funcionalidade desabilitada ou produto não configurado');
      return;
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.attachListeners();
      });
    } else {
      this.attachListeners();
    }
  }

  attachListeners() {
    console.log('[GiftBagManager DEBUG] Anexando listeners');

    // Listener para atualização do carrinho
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== 'gift-bag-manager') {
          console.log('[GiftBagManager DEBUG] cartUpdate detectado de:', event.source);
          this.syncCartState();
        }
      });
    }

    // Listener para botão de adicionar sacola
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.giftBagAddButton');
      if (button) {
        e.preventDefault();
        this.handleAddGiftBag(button);
      }
    });

    this.syncCartState();
  }

  /**
   * Verifica se existe sacola no carrinho
   */
  async hasGiftBagInCart() {
    const cart = await this.fetchCart();
    return cart.items.some((item) => this.isGiftBagItem(item));
  }

  /**
   * Verifica se item e um produto regular do carrinho
   */
  isRegularCartItem(item) {
    const properties = item.properties || {};
    return properties._gift !== 'true' && properties[GIFT_BAG_PROPERTIES.identifier] !== 'true';
  }

  /**
   * Verifica se carrinho tem ao menos um produto regular
   */
  hasRegularProducts(cart) {
    return cart.items.some((item) => this.isRegularCartItem(item));
  }

  /**
   * Verifica se um item é uma sacola de presente
   */
  isGiftBagItem(item) {
    if (!item.properties) return false;
    return item.properties[GIFT_BAG_PROPERTIES.identifier] === 'true';
  }

  /**
   * Obtém a sacola do carrinho (se existir)
   */
  async getGiftBagFromCart() {
    const cart = await this.fetchCart();
    return cart.items.find((item) => this.isGiftBagItem(item));
  }

  /**
   * Obtém todas as linhas de sacola no carrinho
   */
  async getGiftBagItemsFromCart() {
    const cart = await this.fetchCart();
    return cart.items.filter((item) => this.isGiftBagItem(item));
  }

  /**
   * Valida se pode adicionar sacola
   */
  async canAddGiftBag() {
    // Verifica se já está processando
    if (this.isProcessing) {
      console.log('[GiftBagManager DEBUG] Já está processando uma operação');
      return false;
    }

    const cart = await this.fetchCart();
    if (!this.hasRegularProducts(cart)) {
      console.log('[GiftBagManager DEBUG] Sacola indisponível sem produto regular no carrinho');
      return false;
    }

    // Permite múltiplas sacolas no carrinho
    return true;
  }

  /**
   * Adiciona sacola ao carrinho
   */
  async handleAddGiftBag(button = null) {
    console.log('[GiftBagManager DEBUG] handleAddGiftBag chamado');

    // Valida se pode adicionar
    if (!(await this.canAddGiftBag())) {
      return;
    }

    this.isProcessing = true;
    let addedSuccessfully = false;
    this.setButtonLoadingState(button, true);

    try {
      // Busca ID da variante do produto
      const variantId = await this.getGiftBagVariantId();

      if (!variantId) {
        console.error('[GiftBagManager] Produto da sacola não encontrado:', this.config.productHandle);
        this.showError('Produto não encontrado');
        return;
      }

      // Adiciona ao carrinho
      const result = await this.addToCart(variantId);

      if (result) {
        console.log('[GiftBagManager DEBUG] Sacola adicionada com sucesso');
        addedSuccessfully = true;
        this.setButtonVisualState(button, true);
        await this.refreshCartUI();
        this.updateVitrineState();

        // Publica evento de atualização do carrinho
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'gift-bag-manager',
            message: 'Sacola adicionada',
          });
        }
      }
    } catch (error) {
      console.error('[GiftBagManager] Erro ao adicionar sacola:', error);
      this.showError('Erro ao adicionar sacola');
    } finally {
      if (!addedSuccessfully) {
        this.setButtonVisualState(button, false);
      }
      this.setButtonLoadingState(button, false);
      this.isProcessing = false;
    }
  }

  /**
   * Obtém ID da variante do produto sacola
   */
  async getGiftBagVariantId() {
    try {
      const response = await fetch(`/products/${this.config.productHandle}.js`);
      const productData = await response.json();
      return productData?.variants?.[0]?.id;
    } catch (error) {
      console.error('[GiftBagManager] Erro ao buscar produto:', error);
      return null;
    }
  }

  /**
   * Adiciona produto ao carrinho com propriedades da sacola
   */
  async addToCart(variantId) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append(`properties[${GIFT_BAG_PROPERTIES.identifier}]`, 'true');
      formData.append(`properties[${GIFT_BAG_PROPERTIES.locked}]`, 'true');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      const data = await response.json();

      if (data.status) {
        console.error('[GiftBagManager] Erro ao adicionar ao carrinho:', data);
        return null;
      }

      console.log('[GiftBagManager DEBUG] Sacola adicionada. Variante:', variantId, 'Chave:', data.key);
      return data.key;
    } catch (error) {
      console.error('[GiftBagManager] Exceção ao adicionar ao carrinho:', error);
      return null;
    }
  }

  /**
   * Atualiza estado da vitrine (botão e ícones)
   * Agora permite múltiplas sacolas no carrinho
   */
  async updateVitrineState() {
    const vitrine = document.querySelector('.giftBagVitrine');
    if (!vitrine) return;

    const button = vitrine.querySelector('.giftBagAddButton');

    if (button) {
      // Mantém o botão sempre habilitado para permitir múltiplas sacolas
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
      button.classList.remove('giftBagAddButton--disabled');
      this.setButtonVisualState(button, false);
    }
  }

  setButtonVisualState(button, isActive) {
    if (!button) return;

    const svgOriginal = button.querySelector('.svg-original');
    const svgActive = button.querySelector('.svg-active');

    button.setAttribute('data-clicked', isActive ? 'true' : 'false');

    if (svgOriginal) {
      svgOriginal.classList.toggle('hidden', isActive);
      svgOriginal.classList.toggle('block', !isActive);
    }

    if (svgActive) {
      svgActive.classList.toggle('hidden', !isActive);
      svgActive.classList.toggle('block', isActive);
    }
  }

  setButtonLoadingState(button, isLoading) {
    if (!button) return;

    const spinner = button.querySelector('.loading__spinner');

    button.disabled = isLoading;
    button.classList.toggle('loading', isLoading);
    button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');

    if (spinner) {
      spinner.classList.toggle('hidden', !isLoading);
    }
  }

  /**
   * Sincroniza vitrine e limpeza da sacola com o estado real do carrinho
   */
  async syncCartState() {
    if (this.isProcessing) return;

    const cart = await this.fetchCart();
    const hasRegularProducts = this.hasRegularProducts(cart);
    const giftBagItems = cart.items.filter((item) => this.isGiftBagItem(item));

    if (!hasRegularProducts && giftBagItems.length > 0) {
      console.log('[GiftBagManager DEBUG] Carrinho sem produtos regulares. Removendo sacola(s)...');
      await this.removeGiftBags(giftBagItems);
      return;
    }

    this.updateVitrineState();
  }

  /**
   * Exibe mensagem de erro
   */
  showError(message) {
    const errorContainer = document.querySelector('.giftBagError');
    if (!errorContainer) return;

    errorContainer.textContent = message;
    errorContainer.style.display = 'block';

    // Esconde mensagem após 3 segundos
    setTimeout(() => {
      errorContainer.style.display = 'none';
    }, 3000);
  }

  /**
   * Atualiza UI do carrinho
   */
  async refreshCartUI() {
    console.log('[GiftBagManager DEBUG] Atualizando UI do carrinho');

    try {
      const url = `${window.routes.cart_url}?section_id=cart-drawer`;
      const response = await fetch(url);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Atualiza container de conteúdo
      const newContentContainer = doc.querySelector('.cartDrawer__contentContainer');
      const currentContentContainer = document.querySelector('.cartDrawer__contentContainer');

      if (newContentContainer && currentContentContainer) {
        currentContentContainer.innerHTML = newContentContainer.innerHTML;
      }

      // Atualiza footer desktop
      const newFooterDesktop = doc.querySelector('.cartDrawer__footer--desktop');
      const currentFooterDesktop = document.querySelector('.cartDrawer__footer--desktop');

      if (newFooterDesktop && currentFooterDesktop) {
        currentFooterDesktop.innerHTML = newFooterDesktop.innerHTML;
      }

      // Atualiza footer mobile
      const newFooterMobile = doc.querySelector('.cartDrawer__footer--mobile');
      const currentFooterMobile = document.querySelector('.cartDrawer__footer--mobile');

      if (newFooterMobile && currentFooterMobile) {
        currentFooterMobile.innerHTML = newFooterMobile.innerHTML;
      }

      // Atualiza classe is-empty
      const cartDrawerElement = document.querySelector('cart-drawer');
      const newCartDrawer = doc.querySelector('cart-drawer');

      if (cartDrawerElement && newCartDrawer) {
        if (newCartDrawer.classList.contains('is-empty')) {
          cartDrawerElement.classList.add('is-empty');
        } else {
          cartDrawerElement.classList.remove('is-empty');
        }
      }

      console.log('[GiftBagManager DEBUG] UI atualizada');
    } catch (error) {
      console.error('[GiftBagManager] Erro ao atualizar UI:', error);
    }
  }

  /**
   * Busca dados do carrinho
   */
  async fetchCart() {
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      return await response.json();
    } catch (error) {
      console.error('[GiftBagManager] Erro ao buscar carrinho:', error);
      return { items: [] };
    }
  }

  /**
   * Remove sacola do carrinho
   * Chamado automaticamente pelo sistema de limpeza se necessário
   */
  async removeGiftBag() {
    console.log('[GiftBagManager DEBUG] Removendo sacola do carrinho');

    const giftBag = await this.getGiftBagFromCart();
    if (!giftBag) {
      console.log('[GiftBagManager DEBUG] Nenhuma sacola encontrada para remover');
      return;
    }

    try {
      // Adiciona classe de carregamento ao item
      const itemElement = document.querySelector(`[data-cart-item-key="${giftBag.key}"]`);
      if (itemElement) {
        itemElement.classList.add('is-removing');
      }

      await this.updateCartItemQuantity(giftBag.key, 0);
      console.log('[GiftBagManager DEBUG] Sacola removida com sucesso');

      // Publica evento de atualização
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'gift-bag-manager',
          message: 'Sacola removida',
        });
      }
    } catch (error) {
      console.error('[GiftBagManager] Erro ao remover sacola:', error);
      // Remove classe de carregamento em caso de erro
      const itemElement = document.querySelector(`[data-cart-item-key="${giftBag.key}"]`);
      if (itemElement) {
        itemElement.classList.remove('is-removing');
      }
    }
  }

  /**
   * Remove todas as sacolas do carrinho
   */
  async removeGiftBags(giftBagItems = null) {
    const items = giftBagItems || (await this.getGiftBagItemsFromCart());

    if (!items.length) {
      console.log('[GiftBagManager DEBUG] Nenhuma sacola encontrada para remover');
      return;
    }

    this.isProcessing = true;

    try {
      // Adiciona classe de carregamento a todos os itens
      items.forEach((item) => {
        const itemElement = document.querySelector(`[data-cart-item-key="${item.key}"]`);
        if (itemElement) {
          itemElement.classList.add('is-removing');
        }
      });

      for (const giftBag of items) {
        await this.updateCartItemQuantity(giftBag.key, 0);
      }

      await this.refreshCartUI();

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'gift-bag-manager',
          message: 'Sacola removida',
        });
      }
    } catch (error) {
      console.error('[GiftBagManager] Erro ao remover sacolas:', error);
      // Remove classe de carregamento em caso de erro
      items.forEach((item) => {
        const itemElement = document.querySelector(`[data-cart-item-key="${item.key}"]`);
        if (itemElement) {
          itemElement.classList.remove('is-removing');
        }
      });
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Atualiza quantidade de item no carrinho
   */
  async updateCartItemQuantity(itemKey, quantity) {
    try {
      const body = JSON.stringify({
        id: itemKey,
        quantity: quantity,
      });

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = body;

      await fetch(`${routes.cart_change_url}`, config);
    } catch (error) {
      console.error('[GiftBagManager] Erro ao atualizar quantidade:', error);
    }
  }
}

// Inicializa o gerenciador
const giftBagManager = new GiftBagManager();

// Exporta para uso externo
export default giftBagManager;
